import React, { forwardRef } from 'react'
import styled from 'styled-components'

/*
 * The one toast every message uses, whatever its kind.
 *
 * Before, a success was a white card with a check and a close button while an
 * error was notistack's default red pill with neither, so the two looked like
 * different products and an error could not be dismissed. Now every kind is the
 * same card: a tinted icon says what kind it is, the sentence stays dark so it
 * reads anywhere, and there is always a way to close it.
 *
 * Screen readers: a confirmation is announced politely (`role="status"`), a
 * problem interrupts (`role="alert"`). The colour is never the only signal — the
 * icon and its hidden label carry the kind too.
 */

export const TOAST_TONES = {
	success: { color: '#157A32', tint: '#E8F5EC', label: 'Success' },
	error: { color: '#C62828', tint: '#FDECEC', label: 'Error' },
	warning: { color: '#A15C07', tint: '#FEF3E2', label: 'Warning' },
	info: { color: '#3949AB', tint: '#ECEEFB', label: 'Information' },
}

const toneOf = (variant) => TOAST_TONES[variant] || TOAST_TONES.info

const Card = styled.div`
	box-sizing: border-box;
	display: flex;
	align-items: flex-start;
	gap: 12px;
	width: min(440px, calc(100vw - 32px));
	padding: 12px 8px 12px 14px;
	background: #fff;
	color: #1c1c1f;
	border: 1px solid #e6e6ee;
	border-left: 4px solid ${({ $tone }) => $tone.color};
	border-radius: 12px;
	box-shadow: 0 12px 28px rgba(17, 24, 39, 0.16);
	font-family: inherit;
	pointer-events: auto;

	.toast-icon {
		position: relative;
		flex: none;
		display: grid;
		place-items: center;
		width: 28px;
		height: 28px;
		border-radius: 50%;
		background: ${({ $tone }) => $tone.tint};
		color: ${({ $tone }) => $tone.color};
	}

	.toast-body {
		flex: 1 1 auto;
		min-width: 0;
		padding-top: 4px;
	}

	.toast-title {
		margin: 0;
		font-size: 14px;
		line-height: 1.4;
		font-weight: 600;
		color: #1c1c1f;
		overflow-wrap: anywhere;
		white-space: pre-line;
	}

	.toast-description {
		margin: 2px 0 0;
		font-size: 13px;
		line-height: 1.45;
		color: #55555f;
		overflow-wrap: anywhere;
	}

	.toast-actions {
		display: flex;
		flex-wrap: wrap;
		gap: 4px 12px;
		margin-top: 8px;
	}

	.toast-action {
		font: inherit;
		font-size: 13px;
		font-weight: 650;
		color: ${({ $tone }) => ($tone === TOAST_TONES.error ? '#C62828' : '#3949AB')};
		background: none;
		border: 0;
		padding: 2px 0;
		cursor: pointer;
		text-decoration: underline;
		text-underline-offset: 3px;
	}

	.toast-close {
		flex: none;
		display: grid;
		place-items: center;
		width: 32px;
		height: 32px;
		border: 0;
		border-radius: 8px;
		background: none;
		color: #6b6b76;
		cursor: pointer;
	}

	.toast-close:hover {
		background: #f3f3f6;
		color: #1c1c1f;
	}

	.toast-action:focus-visible,
	.toast-close:focus-visible {
		outline: 2px solid #3949ab;
		outline-offset: 2px;
	}
`

const Icon = ({ variant }) => {
	const common = {
		width: 16,
		height: 16,
		viewBox: '0 0 24 24',
		fill: 'none',
		stroke: 'currentColor',
		strokeWidth: 2.4,
		strokeLinecap: 'round',
		strokeLinejoin: 'round',
		'aria-hidden': true,
	}
	switch (variant) {
		case 'success':
			return (
				<svg {...common}>
					<path d="M5 12.5l4.5 4.5L19 7.5" />
				</svg>
			)
		case 'error':
			return (
				<svg {...common}>
					<path d="M12 7v6" />
					<path d="M12 17h.01" />
				</svg>
			)
		case 'warning':
			return (
				<svg {...common}>
					<path d="M12 8v5" />
					<path d="M12 16.5h.01" />
				</svg>
			)
		default:
			return (
				<svg {...common}>
					<path d="M12 11v6" />
					<path d="M12 7.5h.01" />
				</svg>
			)
	}
}

/**
 * @param message      the main line (string or node)
 * @param description  an optional second line
 * @param actions      `[{ label, onClick, keepOpen }]` — shown under the text
 * @param onClose      closes the toast
 */
export const Toast = forwardRef(({ id, variant = 'info', message, description, actions = [], onClose, closeLabel = 'Dismiss' }, ref) => {
	const tone = toneOf(variant)
	const urgent = variant === 'error' || variant === 'warning'

	return (
		<Card
			ref={ref}
			$tone={tone}
			role={urgent ? 'alert' : 'status'}
			aria-live={urgent ? 'assertive' : 'polite'}
			aria-atomic="true"
			data-testid="toast"
			data-variant={variant}
			data-toast-id={id}
		>
			<span className="toast-icon">
				<Icon variant={variant} />
				<span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>{tone.label}:</span>
			</span>
			<div className="toast-body">
				<div className="toast-title">{message}</div>
				{description ? <p className="toast-description">{description}</p> : null}
				{actions.length > 0 && (
					<div className="toast-actions">
						{actions.map(({ label, onClick, keepOpen }) => (
							<button
								key={label}
								type="button"
								className="toast-action"
								onClick={() => {
									if (typeof onClick === 'function') onClick()
									if (!keepOpen && onClose) onClose()
								}}
							>
								{label}
							</button>
						))}
					</div>
				)}
			</div>
			<button type="button" className="toast-close" onClick={() => onClose && onClose()} aria-label={closeLabel} title={closeLabel} data-testid="toast-close">
				<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
					<path d="M6 6l12 12M18 6L6 18" />
				</svg>
			</button>
		</Card>
	)
})

Toast.displayName = 'Toast'
